import { styled } from "styled-components";
import { theme } from "../../theme";
import { flexCenterCenter } from "../../style/mixins";

interface PagingProps {
  pageIndex?: number;
  totalPage: number;
  onPageChange: (page: number) => void;
}

export default function Paging({ pageIndex = 1, totalPage, onPageChange }: PagingProps) {
  const MAX_PAGE_DISPLAY = 10;
  let startPage = Math.max(1, pageIndex - Math.floor(MAX_PAGE_DISPLAY / 2));
  let endPage = startPage + MAX_PAGE_DISPLAY - 1;

  if (endPage > totalPage) {
    endPage = totalPage;
    startPage = Math.max(1, endPage - MAX_PAGE_DISPLAY + 1);
  }

  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  return (
    <PagingLayout>
      {pageIndex > 2 && (
        <PagingBtn onClick={() => onPageChange(1)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="24"
            viewBox="0 0 12 24"
            fill="none"
          >
            <path
              d="M11.548 17.42L10.487 18.48L4.70801 12.703C4.61486 12.6104 4.54093 12.5004 4.49048 12.3791C4.44003 12.2579 4.41406 12.1278 4.41406 11.9965C4.41406 11.8652 4.44003 11.7352 4.49048 11.6139C4.54093 11.4927 4.61486 11.3826 4.70801 11.29L10.487 5.51001L11.547 6.57001L6.12301 11.995L11.548 17.42Z"
              fill="#999999"
            />
            <path
              d="M8.13395 17.42L7.07295 18.48L1.29395 12.703C1.2008 12.6104 1.12687 12.5004 1.07642 12.3791C1.02597 12.2579 0.999999 12.1278 0.999999 11.9965C0.999999 11.8652 1.02597 11.7352 1.07642 11.6139C1.12687 11.4927 1.2008 11.3826 1.29395 11.29L7.07295 5.51001L8.13295 6.57001L2.70895 11.995L8.13395 17.42Z"
              fill="#999999"
            />
          </svg>
        </PagingBtn>
      )}

      {/* 이전 페이지 이동 버튼 */}
      {pageIndex > 1 && (
        <PagingBtn onClick={() => onPageChange(pageIndex - 1)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="24"
            viewBox="0 0 12 24"
            fill="none"
          >
            <path
              d="M9.54801 17.42L8.48701 18.48L2.70801 12.703C2.61486 12.6104 2.54093 12.5004 2.49048 12.3791C2.44003 12.2579 2.41406 12.1278 2.41406 11.9965C2.41406 11.8652 2.44003 11.7352 2.49048 11.6139C2.54093 11.4927 2.61486 11.3826 2.70801 11.29L8.48701 5.51001L9.54701 6.57001L4.12301 11.995L9.54801 17.42Z"
              fill="#999999"
            />
          </svg>
        </PagingBtn>
      )}

      {/* 페이지 번호 버튼들 */}
      {pageNumbers.map((num) => (
        <PagingBtn
          key={num}
          onClick={pageIndex === num ? () => {} : () => onPageChange(num)}
          $action={pageIndex === num}
        >
          {num}
        </PagingBtn>
      ))}

      {/* 다음 페이지 이동 버튼 */}
      {pageIndex < totalPage && (
        <PagingBtn onClick={() => onPageChange(pageIndex + 1)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="24"
            viewBox="0 0 12 24"
            fill="none"
          >
            <path
              d="M2.45199 6.57999L3.51299 5.51999L9.29199 11.297C9.38514 11.3896 9.45907 11.4996 9.50952 11.6209C9.55997 11.7421 9.58594 11.8722 9.58594 12.0035C9.58594 12.1348 9.55997 12.2648 9.50952 12.3861C9.45907 12.5073 9.38514 12.6174 9.29199 12.71L3.51299 18.49L2.45299 17.43L7.87699 12.005L2.45199 6.57999Z"
              fill="#666666"
            />
          </svg>
        </PagingBtn>
      )}

      {/* 마지막 페이지(또는 맨 끝으로 이동) 버튼 예시 - 필요하면 활성화 */}
      {pageIndex < totalPage - 1 && (
        <PagingBtn onClick={() => onPageChange(totalPage)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="24"
            viewBox="0 0 12 24"
            fill="none"
          >
            <path
              d="M0.451988 6.57999L1.51299 5.51999L7.29199 11.297C7.38514 11.3896 7.45907 11.4996 7.50952 11.6209C7.55997 11.7421 7.58594 11.8722 7.58594 12.0035C7.58594 12.1348 7.55997 12.2648 7.50952 12.3861C7.45907 12.5073 7.38514 12.6174 7.29199 12.71L1.51299 18.49L0.452986 17.43L5.87699 12.005L0.451988 6.57999Z"
              fill="#666666"
            />
            <path
              d="M3.86605 6.57999L4.92705 5.51999L10.7061 11.297C10.7992 11.3896 10.8731 11.4996 10.9236 11.6209C10.974 11.7421 11 11.8722 11 12.0035C11 12.1348 10.974 12.2648 10.9236 12.3861C10.8731 12.5073 10.7992 12.6174 10.7061 12.71L4.92705 18.49L3.86705 17.43L9.29105 12.005L3.86605 6.57999Z"
              fill="#666666"
            />
          </svg>
        </PagingBtn>
      )}
    </PagingLayout>
  );
}

const PagingLayout = styled.ul`
  padding: 0;
  margin: 0;
  display: flex;
  justify-content: center;
  gap: 0.25rem;
`;

const PagingBtn = styled.li<{ $action?: boolean }>`
  color: ${({ $action }) => ($action ? "#fff" : "#999")};
  background-color: ${({ $action }) => ($action ? theme.color.primary : "#fff")};
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 50%;
  ${flexCenterCenter};
  list-style-type: none;
  cursor: pointer;

  &:hover {
    color: #fff;
    background-color: ${theme.color.primary};

    path {
      fill: #fff;
    }
  }
`;
